import { useApolloClient } from '@apollo/client'
import { type SheetThread, SheetThreadsDocument, type ViewFieldsFragment } from '@salescore/client-api'
import { useMe } from '@salescore/client-common'
import { CORE_CONSTANT } from '@salescore/core'
import { organizationHasFeature } from '@salescore/frontend-common'
import { useRecoilValue } from 'recoil'

import { viewsContextAtom } from '../../view/atoms'

function groupByRecordId(threads: SheetThread[]): Map<string, SheetThread[]> {
  const grouped = new Map<string, SheetThread[]>()
  threads.forEach((thread) => {
    const group = grouped.get(thread.recordId)
    if (group === undefined) {
      grouped.set(thread.recordId, [thread])
    } else {
      grouped.set(thread.recordId, [...group, thread])
    }
  })
  return grouped
}

export const useUpdateSheetThreadsCache = (view: ViewFieldsFragment) => {
  const me = useMe()
  const views = useRecoilValue(viewsContextAtom)
  const commentFeatureEnabled = organizationHasFeature(me.organization, 'enable_slack_comment')
  const apolloClient = useApolloClient()

  return async (recordIds: string[]) => {
    const shouldFetch =
      commentFeatureEnabled && // コメント機能が有効かつ
      view.config.type === 'sheet' && // シートビューである場合、
      views.views[0]?.type !== 'kpi' && // ただし、KPI タブ内のシートではなく、
      !view.id.startsWith(CORE_CONSTANT.KPI_PREVIEW_DRILL_DOWN_VIEW_ID_PREFIX) && // KPIプレビューからのドリルダウンでもなく
      view.id !== '' // 目標設定でもない場合

    if (!shouldFetch) {
      return
    }
    const { data, error } = await apolloClient.query({
      query: SheetThreadsDocument,
      variables: {
        organizationId: me.organization.id,
        recordIds,
      },
    })
    if (error !== undefined) {
      return
    }
    const groupedThreads = groupByRecordId(data.sheetThreads)
    for (const recordId of recordIds) {
      apolloClient.cache.writeQuery({
        query: SheetThreadsDocument,
        variables: {
          organizationId: me.organization.id,
          recordIds: [recordId],
        },
        data: {
          sheetThreads: groupedThreads.get(recordId) ?? [],
        },
      })
    }
  }
}
